// app/components/FilterDrawer.jsx
import { useRef } from "react";
import QuickShopDualRangeSlider from "./QuickShopDualRangeSlider";

const CATEGORIES = ["Art Supplies", "Books", "Classroom Basics", "Educational Games", "Food, Clothing & Hygiene","Instructional Technology","STEM","Decor","Furniture","Other"];
const CATEGORY_EMOJI = {
  "Art Supplies":             "🎨",
  "Books":                    "📚",
  "Classroom Basics":         "🏫",
  "Educational Games":        "🎮",
  "Food, Clothing & Hygiene": "🍎",
  "Instructional Technology": "🔌",
  "STEM":                     "🔬",
  "Decor":                    "🌟",
  "Furniture":                "🪑",
  "Other":                    "📦",
};
const US_STATES = [
  "AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA",
  "KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ",
  "NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT",
  "VA","WA","WV","WI","WY","DC",
];

const GRADE_GROUPS = [
  { label: "Pre-K – K", grades: ["PK","K", "PreK","PreK-5"]                  },
  { label: "1 – 5",     grades: ["1","2","3","4","5","K-5","PreK-5","3,5","K-8","5-8"]},
  { label: "6 – 8",     grades: ["6","7","8","6,7,8","6-8","K-8","5-8"]},
  { label: "9 – 12",    grades: ["9","10","11","12", "9-12", "9 - 12"]},
];

export default function FilterDrawer({ open, filters, onChange, DEFAULT_QS_FILTERS }) {
  const debounceRef = useRef(null);

  const debounced = (key, value) => {
    const next = { ...filters, [key]: value };
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => onChange(next), 350);
  };

  const toggle = (key, val) => {
    const arr  = filters[key];
    const next = arr.includes(val) ? arr.filter(v => v !== val) : [...arr, val];
    onChange({ ...filters, [key]: next });
  };

  const toggleGroup = (label, vals) => {
    const selectedLabels = filters.gradeGroupLabels || [];
    const isActive = selectedLabels.includes(label);
    const nextLabels = isActive
      ? selectedLabels.filter(l => l !== label)
      : [...selectedLabels, label];

    // Recompute the actual grade codes sent to the backend
    // as the union of all grades in the currently-selected groups.
    const nextGrades = [...new Set(
      nextLabels.flatMap(l => GRADE_GROUPS.find(g => g.label === l).grades)
    )];

    onChange({ ...filters, gradeGroupLabels: nextLabels, grades: nextGrades });
  };

  // ✅ Route ranges through structural debouncing to stop API freezing
  const handleRangeChangeDebounced = (minKey, maxKey, minVal, maxVal) => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      onChange({
        ...filters,
        [minKey]: minVal,
        [maxKey]: maxVal
      });
    }, 350);
  };

  const hasActive = filters.searchTerm.length > 0 || filters.categories.length || filters.states.length || (filters.gradeGroupLabels || []).length
    || filters.itemMinPrice > 0 || filters.itemMaxPrice < 500
    || filters.minEnrollment > 0 || filters.maxEnrollment < 2000
    || filters.minLunchPct > 0   || filters.maxLunchPct < 100;

  return (
    <div style={{
      overflow: "hidden",
      maxHeight: open ? 950 : 0, 
      opacity:   open ? 1 : 0,
      transition: "max-height .35s cubic-bezier(.4,0,.2,1), opacity .25s ease",
    }}>
      <div style={{
        background: "#fffbf5", border: "2px solid #fde68a",
        borderRadius: 16, padding: "16px 20px 14px",
        marginBottom: 20, position: "relative", boxShadow: "3px 3px 0 #fde68a",
      }}>
        {/* Notebook lines */}
        {[...Array(20)].map((_, i) => (
          <div key={i} style={{
            position: "absolute", left: 0, right: 0, top: 28 + i * 32,
            height: 1, background: "rgba(147,197,253,0.15)", pointerEvents: "none",
          }} />
        ))}

        <div style={{ display: "flex", gap: 16, alignItems: "flex-start", position: "relative" }}>

          {/* LEFT column */}
          <div style={{ flexShrink: 0, minWidth: 0, display: "flex", flexDirection: "column", gap: 12, width: 290 }}>
            
            {/* Search */}
            <div>
              <div style={{ fontSize: 10, fontWeight: 800, color: "#dc2626", textTransform: "uppercase", letterSpacing: "1px", marginBottom: 6 }}>
                🔍 Search
              </div>
              <input
                type="text"
                placeholder="Type 'clips', 'science'..."
                defaultValue={filters.searchTerm}
                onChange={(e) => debounced("searchTerm", e.target.value)}
                style={{
                  width: "100%", padding: "6px 12px", borderRadius: "12px",
                  border: "2px solid #fde68a", background: "#fff",
                  fontSize: "12px", outline: "none", color: "#92400e",
                  fontWeight: "600", boxShadow: "inset 0 1px 2px rgba(0,0,0,0.05)"
                }}
              />
            </div>

            {/* Category */}
            <div>
              <div style={{ fontSize: 10, fontWeight: 800, color: "#dc2626", textTransform: "uppercase", letterSpacing: "1px", marginBottom: 6 }}>
                📦 Category
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                {CATEGORIES.map(cat => {
                  const active = filters.categories.includes(cat);
                  return (
                    <button key={cat} onClick={() => toggle("categories", cat)} style={{
                      padding: "3px 9px", borderRadius: 999, cursor: "pointer",
                      fontSize: 11, fontWeight: 700,
                      background: active ? "#dc2626" : "#fff9e6",
                      color:       active ? "#fff"    : "#92400e",
                      border:      active ? "2px solid #dc2626" : "2px solid #fde68a",
                      transition: "all .12s",
                    }}>
                      {CATEGORY_EMOJI[cat]} {cat}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Grade */}
            <div>
              <div style={{ fontSize: 10, fontWeight: 800, color: "#dc2626", textTransform: "uppercase", letterSpacing: "1px", marginBottom: 6 }}>
                🎓 Grade
              </div>
              <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
                {GRADE_GROUPS.map(g => {
                  const active = (filters.gradeGroupLabels || []).includes(g.label);
                  return (
                    <button key={g.label} onClick={() => toggleGroup(g.label, g.grades)} style={{
                      padding: "3px 12px", borderRadius: 999, cursor: "pointer",
                      fontSize: 11, fontWeight: 700,
                      background: active ? "#f97316" : "#fff9e6",
                      color:       active ? "#fff"    : "#92400e",
                      border:      active ? "2px solid #f97316" : "2px solid #fde68a",
                      transition: "all .12s",
                    }}>
                      {g.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Sliders Container */}
            <div style={{ display: "flex", flexDirection: "column", gap: 14, paddingTop: 10, borderTop: "1px dashed #fde68a" }}>
              
              <div>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10, fontWeight: 800, color: "#dc2626", textTransform: "uppercase", letterSpacing: "1px", marginBottom: 4 }}>
                  <span>🏷️ Item Price</span>
                  <span style={{ color: "#92400e" }}>${filters.itemMinPrice} - ${filters.itemMaxPrice}</span>
                </div>
                <QuickShopDualRangeSlider
                  min={0}
                  max={500}
                  minVal={filters.itemMinPrice}
                  maxVal={filters.itemMaxPrice}
                  onChange={({ min, max }) => handleRangeChangeDebounced("itemMinPrice", "itemMaxPrice", min, max)}
                />
              </div>

              <div>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10, fontWeight: 800, color: "#dc2626", textTransform: "uppercase", letterSpacing: "1px", marginBottom: 4 }}>
                  <span>🏫 School Size</span>
                  <span style={{ color: "#92400e" }}>{filters.minEnrollment} - {filters.maxEnrollment} e.</span>
                </div>
                <QuickShopDualRangeSlider
                  min={0}
                  max={2000}
                  minVal={filters.minEnrollment}
                  maxVal={filters.maxEnrollment}
                  onChange={({ min, max }) => handleRangeChangeDebounced("minEnrollment", "maxEnrollment", min, max)}
                />
              </div>

              <div>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10, fontWeight: 800, color: "#dc2626", textTransform: "uppercase", letterSpacing: "1px", marginBottom: 4 }}>
                  <span>🍎 Lunch %</span>
                  <span style={{ color: "#92400e" }}>{filters.minLunchPct}% - {filters.maxLunchPct}%</span>
                </div>
                <QuickShopDualRangeSlider
                  min={0}
                  max={100}
                  minVal={filters.minLunchPct}
                  maxVal={filters.maxLunchPct}
                  onChange={({ min, max }) => handleRangeChangeDebounced("minLunchPct", "maxLunchPct", min, max)}
                />
              </div>

            </div>

          </div>

          {/* Vertical divider */}
          <div style={{ width: 1, background: "#fde68a", alignSelf: "stretch", flexShrink: 0 }} />

          {/* RIGHT column */}
          <div style={{ flexShrink: 0, maxWidth: 290 }}>
            <div style={{ fontSize: 10, fontWeight: 800, color: "#dc2626", textTransform: "uppercase", letterSpacing: "1px", marginBottom: 6 }}>
              📍 State
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 3 }}>
              {US_STATES.map(s => {
                const active = filters.states.includes(s);
                return (
                  <button key={s} onClick={() => toggle("states", s)} style={{
                    width: 28, height: 22, borderRadius: 4,
                    cursor: "pointer", fontSize: 8, fontWeight: 800, padding: 0,
                    background: active ? "#dc2626" : "#fff9e6",
                    color:       active ? "#fff"    : "#92400e",
                    border:      active ? "2px solid #dc2626" : "2px solid #fde68a",
                    transition: "all .1s",
                  }}>
                    {s}
                  </button>
                );
              })}
            </div>
          </div>

        </div>

        {/* Form Reset Button */}
        {hasActive && (
          <button onClick={() => onChange(DEFAULT_QS_FILTERS)} style={{
            marginTop: 16, fontSize: 11, fontWeight: 800,
            color: "#dc2626", background: "#fef2f2",
            border: "2px solid #fca5a5", borderRadius: 999,
            padding: "3px 12px", cursor: "pointer",
            position: "relative", zIndex: 10
          }}>
            Reset filters ✕
          </button>
        )}
      </div>
    </div>
  );
}